import { useMemo, useState } from 'react';
import { useConnectStore } from '../../../../store/models';
import useIntegrationDetails from './useIntegrationDetails';

/*
 * Normalises a raw browse-integrations record into the fields the card UI needs.
 * initials falls back to a name-derived value when the BE doesn't supply one.
 * detailsByName supplies the CDN-hosted subtitle/summary/features, keyed by
 * lower-cased/trimmed name; a missing match (no CDN entry, or fetch still
 * pending/failed) falls back to empty values.
 */
const FALLBACK_MESSAGE = "New App integration coming soon...";

function normalize (raw, index, detailsByName) {
    const name = raw.name || '';
    const details = detailsByName[name.trim().toLowerCase()] || {};
    return {
        id: raw.id || `integration-${index}`,
        name,
        category: raw.category || '',
        description: raw.description || '',
        logo: raw.logo || '',
        initials: raw.initials || (name.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase(),
        subtitle: details.subtitle || ' ',
        summary: details.summary || FALLBACK_MESSAGE,
        features: details.features || [],
    };
}

function useConnectIntegrations () {
    const connectStore = useConnectStore();
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('All');
    const { integrationDetails } = useIntegrationDetails();
console.log('test print -> ', integrationDetails)
    // integrationsLoading is a real boolean; integrations is a uuid change-token
    // (set once browseConnectIntegrations resolves) coerced to a "resolved" boolean.
    const loading = connectStore.observed.integrationsLoading;
    const resolved = !!connectStore.observed.integrations;

    const integrations = useMemo(
        () => (connectStore.integrations || []).map((raw, index) => normalize(raw, index, integrationDetails)),
        // re-derive when the store list token bumps, or the CDN details resolve
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [connectStore.observed.integrations, integrationDetails]
    );

    // A record may belong to several categories, e.g. "CRM / Marketing Automation".
    const categories = useMemo(() => {
        const set = new Set();
        integrations.forEach((i) => {
            i.category.split(' / ').forEach((c) => {
                const trimmed = c.trim();
                if (trimmed) set.add(trimmed);
            });
        });
        return ['All', ...Array.from(set).sort()];
    }, [integrations]);

    const filtered = useMemo(() => integrations.filter((i) => {
        const cats = i.category.split(' / ').map((c) => c.trim());
        return (category === 'All' || cats.includes(category)) &&
            (query === '' || i.name.toLowerCase().includes(query.toLowerCase()));
    }), [integrations, category, query]);

    return {
        loading: loading || !resolved,
        query,
        setQuery,
        category,
        setCategory,
        categories,
        filtered,
    };
}

export default useConnectIntegrations;
